import { useSearchParams } from 'react-router-dom';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG, NAVIGATION_REDESIGN } from '../utils/constants';
import { useState } from 'react';
import { AmbassadorOffboardingForm } from '../components/ambassadors/ambassadorOffboardingForm';
import { AmbassadorOffboardingConfirmation } from '../components/ambassadors/ambassadorOffboardingConfirmation';
import { useUser } from '../hooks/user';
import { useGetAmbassador } from '../hooks/ambassadors/getAmbassador';
import {
  Alert,
  AppLayout,
  ContentLayout,
  Header,
  Link,
  Spinner,
} from '@amzn/awsui-components-react';
import { useLocalizedText } from '../hooks/localizedText';
import { getHttpStatusCode } from '../utils/errorHelper';

export function AmbassadorOffboarding() {
  const [submitted, setSubmitted] = useState(false);
  const { t } = useLocalizedText(['ambassadors']);
  const user = useUser();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const {
    data: ambassadorData,
    isLoading: isLoadingAmbassador,
    isError,
    error: ambassadorError,
  } = useGetAmbassador(user.employeeId || '');
  const isNotFound = getHttpStatusCode(ambassadorError) === 404;
  const hasData = ambassadorData !== undefined;
  const isRegistered = ambassadorData?.ambassador?.isRegistered === true;

  if (submitted) {
    return (
      <div data-testid="ambassador-offboarding-page">
        <AmbassadorOffboardingConfirmation />
      </div>
    );
  }

  if (isLoadingAmbassador || (isError && !isNotFound) || !hasData || !isRegistered) {
    const loadingContent = (
      <ContentLayout
        header={
          <Header variant="h1" data-testid="ambassador-offboard-page-header">
            {t('ambassadorOffboarding.leaveTheCommunityAmbassadorProgram')}
          </Header>
        }
      >
        {isLoadingAmbassador || (!hasData && !isError) ? (
          <Spinner />
        ) : isError && !isNotFound ? (
          <Alert type="error">{t('ambassadorOffboarding.loadFailed')}</Alert>
        ) : (
          <Alert
            type="info"
            data-testid="ambassador-offboarding-not-registered-alert"
            header={t('ambassadorOffboarding.notRegistered.header')}
          >
            {t('ambassadorOffboarding.notRegistered.message')}{' '}
            <Link href="/ambassadors/register">
              {t('ambassadorOffboarding.notRegistered.register')}
            </Link>
          </Alert>
        )}
      </ContentLayout>
    );

    // New navigation: return content only, shell handled by root AppLayout
    if (isNewNav) {
      return loadingContent;
    }

    // Legacy: keep existing AppLayout wrapper
    return <AppLayout navigationHide toolsHide content={loadingContent} />;
  }

  return (
    <div data-testid="ambassador-offboarding-page">
      <AmbassadorOffboardingForm setSubmitted={setSubmitted} />
    </div>
  );
}

export function AmbassadorOffboardingView() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return <AmbassadorOffboarding />;
  }
  return <></>;
}
